import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  TrendingUp, TrendingDown, Minus, Sparkles, RefreshCw, Download, Search,
  X, ArrowUpRight, ArrowDownRight, AlertTriangle, Target, Zap, Eye,
  Users, Activity, DollarSign, Settings, Brain, ChevronRight, MousePointerClick
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area
} from "recharts";

const COLORS = { green: "#10B981", red: "#EF4444", yellow: "#F59E0B", blue: "#3B82F6", purple: "#A855F7", orange: "#F97316" };

function TrendBadge({ trend, pct }) {
  if (trend === "increasing") return (
    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium">
      <Sparkles className="w-3 h-3" style={{ color: COLORS.green }} /><TrendingUp className="w-3 h-3" />{pct > 0 ? `+${pct}%` : `${pct}%`}
    </div>
  );
  if (trend === "decreasing") return (
    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-medium">
      <Sparkles className="w-3 h-3" style={{ color: COLORS.red }} /><TrendingDown className="w-3 h-3" />{pct}%
    </div>
  );
  return (
    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium">
      <Sparkles className="w-3 h-3" style={{ color: COLORS.yellow }} /><Minus className="w-3 h-3" />Flat
    </div>
  );
}

function RecommendationBadge({ rec }) {
  const map = {
    green: { bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-800", icon: <Zap className="w-3 h-3" /> },
    red: { bg: "bg-red-50 border-red-200", text: "text-red-800", icon: <TrendingDown className="w-3 h-3" /> },
    yellow: { bg: "bg-amber-50 border-amber-200", text: "text-amber-800", icon: <AlertTriangle className="w-3 h-3" /> },
  };
  const style = map[rec.badge] || map.yellow;
  return (
    <div className={`flex items-start gap-2 p-3 rounded-lg border ${style.bg}`}>
      <div className={`mt-0.5 ${style.text}`}>{style.icon}</div>
      <div>
        <div className={`text-xs font-semibold ${style.text}`}>{rec.campaign_type}</div>
        <div className="text-xs text-slate-600 mt-0.5">{rec.summary}</div>
      </div>
    </div>
  );
}

export default function LinkedInABMAgentPage() {
  const { sessionToken } = useAuth();
  const [activeTab, setActiveTab] = useState("insights");
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountDetail, setAccountDetail] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 90);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [thresholds, setThresholds] = useState({ medium_signals: 10, high_signals: 25, growth_rate_trigger: 50, thrashing_threshold: 3 });
  const [savingThresholds, setSavingThresholds] = useState(false);

  const tenantId = sessionStorage.getItem("viewing_tenant_id");
  const headers = { Authorization: `Bearer ${sessionToken}` };

  const fetchData = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [acctRes, insightRes] = await Promise.all([
        axios.get(`${API}/linkedin-abm-agent/accounts-with-trends?tenant_id=${tenantId}&start_date=${startDate}&end_date=${endDate}`, { headers }),
        axios.get(`${API}/linkedin-abm-agent/cached-insight?tenant_id=${tenantId}`, { headers }),
      ]);
      setAccounts(acctRes.data.accounts || []);
      if (acctRes.data.thresholds) setThresholds(acctRes.data.thresholds);
      if (insightRes.data?.insight) setInsight(insightRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [tenantId, startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const generateInsight = async () => {
    setInsightLoading(true);
    try {
      const res = await axios.post(`${API}/linkedin-abm-agent/generate-insights?tenant_id=${tenantId}`, {}, { headers });
      setInsight(res.data);
      toast.success("Weekly ABM briefing generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate insights");
    }
    setInsightLoading(false);
  };

  const exportCSV = async (type) => {
    const url = type === "accounts"
      ? `${API}/linkedin-abm-agent/export/accounts-csv?tenant_id=${tenantId}`
      : `${API}/linkedin-abm-agent/export/dashboard-csv?tenant_id=${tenantId}`;
    try {
      const res = await axios.get(url, { headers, responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data);
      link.download = type === "accounts" ? "unified_accounts.csv" : "dashboard_summary.csv";
      link.click();
      toast.success("CSV downloaded");
    } catch (e) { toast.error("Export failed"); }
  };

  const saveThresholds = async () => {
    setSavingThresholds(true);
    try {
      await axios.put(`${API}/linkedin-abm-agent/thresholds?tenant_id=${tenantId}&medium_signals=${thresholds.medium_signals}&high_signals=${thresholds.high_signals}&growth_rate_trigger=${thresholds.growth_rate_trigger}&thrashing_threshold=${thresholds.thrashing_threshold}`, {}, { headers });
      toast.success("Thresholds saved");
      fetchData();
    } catch (e) { toast.error("Failed to save"); }
    setSavingThresholds(false);
  };

  const fetchAccountDetail = async (domain) => {
    try {
      const res = await axios.get(`${API}/unified-reporting/accounts/${domain}?tenant_id=${tenantId}`, { headers });
      setAccountDetail(res.data);
      setSelectedAccount(domain);
    } catch (e) { toast.error("Failed to load detail"); }
  };

  const increasing = accounts.filter(a => a.trend === "increasing");
  const decreasing = accounts.filter(a => a.trend === "decreasing");
  const flat = accounts.filter(a => a.trend === "flat");
  const filteredAccounts = accounts.filter(a => !searchQuery || a.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) || a.domain?.toLowerCase().includes(searchQuery.toLowerCase()));

  const trendDistribution = [
    { name: "Increasing", value: increasing.length, fill: COLORS.green },
    { name: "Flat", value: flat.length, fill: COLORS.yellow },
    { name: "Decreasing", value: decreasing.length, fill: COLORS.red },
  ].filter(d => d.value > 0);

  const recDistribution = [
    { name: "Lead Gen", value: accounts.filter(a => a.recommendation?.action === "lead_gen").length, fill: COLORS.green },
    { name: "Consideration", value: accounts.filter(a => a.recommendation?.action === "consideration").length, fill: COLORS.blue },
    { name: "Awareness", value: accounts.filter(a => a.recommendation?.action === "awareness").length, fill: COLORS.purple },
    { name: "Maintain", value: accounts.filter(a => a.recommendation?.action === "maintain").length, fill: COLORS.yellow },
    { name: "Reduce", value: accounts.filter(a => a.recommendation?.action === "reduce").length, fill: COLORS.red },
    { name: "Caution", value: accounts.filter(a => a.recommendation?.action === "caution").length, fill: COLORS.orange },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-6" data-testid="linkedin-abm-agent-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">LinkedIn ABM agent</h1>
          <p className="text-sm text-slate-500 mt-1">AI-powered account intelligence and campaign recommendations</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => exportCSV("accounts")} size="sm" variant="outline" data-testid="export-accounts-csv"><Download className="w-4 h-4 mr-1" />Export CSV</Button>
          <Button onClick={generateInsight} disabled={insightLoading} size="sm" className="bg-purple-600 hover:bg-purple-700" data-testid="generate-insights-btn">
            {insightLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Brain className="w-4 h-4 mr-1" />}
            Generate weekly briefing
          </Button>
        </div>
      </div>

      {/* Date range picker */}
      <div className="flex items-center gap-3 bg-slate-50 rounded-lg px-4 py-2.5 border" data-testid="date-range-picker">
        <span className="text-xs font-medium text-slate-500">Date range</span>
        <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-36 h-8 text-xs" data-testid="start-date" />
        <span className="text-xs text-slate-400">to</span>
        <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-36 h-8 text-xs" data-testid="end-date" />
        <Button onClick={fetchData} size="sm" variant="outline" className="h-8"><RefreshCw className="w-3 h-3 mr-1" />Apply</Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="insights" data-testid="tab-insights">AI insights</TabsTrigger>
          <TabsTrigger value="accounts" data-testid="tab-abm-accounts">Accounts ({accounts.length})</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Thresholds</TabsTrigger>
        </TabsList>

        {/* AI INSIGHTS TAB */}
        <TabsContent value="insights" className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-16"><RefreshCw className="w-5 h-5 animate-spin text-slate-400 mr-2" />Loading...</div>
          ) : (
            <>
              {/* KPI cards */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <Card><CardContent className="pt-4 pb-3 px-4">
                  <div className="text-xs text-slate-500">Accounts</div>
                  <div className="text-2xl font-bold">{accounts.length}</div>
                </CardContent></Card>
                <Card className="border-emerald-200"><CardContent className="pt-4 pb-3 px-4">
                  <div className="text-xs text-emerald-600 flex items-center gap-1"><Sparkles className="w-3 h-3" style={{ color: COLORS.green }} />Increasing</div>
                  <div className="text-2xl font-bold text-emerald-700">{increasing.length}</div>
                </CardContent></Card>
                <Card className="border-red-200"><CardContent className="pt-4 pb-3 px-4">
                  <div className="text-xs text-red-600 flex items-center gap-1"><Sparkles className="w-3 h-3" style={{ color: COLORS.red }} />Decreasing</div>
                  <div className="text-2xl font-bold text-red-700">{decreasing.length}</div>
                </CardContent></Card>
                <Card className="border-amber-200"><CardContent className="pt-4 pb-3 px-4">
                  <div className="text-xs text-amber-600 flex items-center gap-1"><Sparkles className="w-3 h-3" style={{ color: COLORS.yellow }} />Flat</div>
                  <div className="text-2xl font-bold text-amber-700">{flat.length}</div>
                </CardContent></Card>
                <Card><CardContent className="pt-4 pb-3 px-4">
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Eye className="w-3 h-3" />Impressions</div>
                  <div className="text-2xl font-bold">{accounts.reduce((s, a) => s + (a.ar_impressions || 0), 0).toLocaleString()}</div>
                </CardContent></Card>
                <Card><CardContent className="pt-4 pb-3 px-4">
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Activity className="w-3 h-3" />Signals</div>
                  <div className="text-2xl font-bold">{accounts.reduce((s, a) => s + (a.cr_total_signals || 0), 0).toLocaleString()}</div>
                </CardContent></Card>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Account trends</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart><Pie data={trendDistribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, value }) => `${name}: ${value}`}>
                        {trendDistribution.map((d, i) => <Cell key={i} fill={d.fill} />)}
                      </Pie><Legend wrapperStyle={{ fontSize: 11 }} /></PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Recommended actions</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart><Pie data={recDistribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, value }) => `${name}: ${value}`}>
                        {recDistribution.map((d, i) => <Cell key={i} fill={d.fill} />)}
                      </Pie><Legend wrapperStyle={{ fontSize: 11 }} /></PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Signal change (30d vs prior 30d)</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={[...accounts].sort((a, b) => b.change_pct - a.change_pct).slice(0, 8)} layout="vertical" margin={{ left: 5, right: 15 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `${v}%`} />
                        <YAxis dataKey="company_name" type="category" tick={{ fontSize: 9 }} width={100} />
                        <Tooltip formatter={v => [`${v}%`, "Change"]} />
                        <Bar dataKey="change_pct" fill="#3B82F6" radius={[0, 4, 4, 0]}>
                          {[...accounts].sort((a, b) => b.change_pct - a.change_pct).slice(0, 8).map((a, i) => (
                            <Cell key={i} fill={a.change_pct > 10 ? COLORS.green : a.change_pct < -10 ? COLORS.red : COLORS.yellow} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* AI Briefing */}
              <Card className="border-purple-200 bg-gradient-to-br from-purple-50/30 to-white">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2"><Brain className="w-4 h-4 text-purple-600" />Weekly ABM intelligence briefing</CardTitle>
                    {insight?.generated_at && <span className="text-[10px] text-slate-400">Generated {new Date(insight.generated_at).toLocaleString()}</span>}
                  </div>
                </CardHeader>
                <CardContent>
                  {insight?.insight ? (
                    <div className="max-w-none text-slate-700 text-sm leading-relaxed space-y-3" data-testid="ai-insight-content">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({ children }) => <h2 className="text-lg font-bold text-slate-900 mt-4 mb-2 border-b pb-1">{children}</h2>,
                          h2: ({ children }) => <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">{children}</h3>,
                          h3: ({ children }) => <h4 className="text-sm font-semibold text-slate-700 mt-3 mb-1">{children}</h4>,
                          p: ({ children }) => <p className="text-sm text-slate-600 mb-2">{children}</p>,
                          ul: ({ children }) => <ul className="space-y-1.5 ml-1">{children}</ul>,
                          li: ({ children }) => <li className="text-sm text-slate-600 flex gap-2"><span className="text-purple-400 mt-1 shrink-0">•</span><span>{children}</span></li>,
                          strong: ({ children }) => <strong className="font-semibold text-slate-800">{children}</strong>,
                          table: ({ children }) => <div className="overflow-x-auto my-3 rounded-lg border"><table className="w-full text-xs">{children}</table></div>,
                          thead: ({ children }) => <thead className="bg-slate-100">{children}</thead>,
                          th: ({ children }) => <th className="px-3 py-2 text-left font-semibold text-slate-700 border-b">{children}</th>,
                          td: ({ children }) => <td className="px-3 py-2 border-b border-slate-50 text-slate-600">{children}</td>,
                          tr: ({ children }) => <tr className="hover:bg-slate-50">{children}</tr>,
                        }}
                      >
                        {insight.insight}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Brain className="w-8 h-8 text-purple-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-500">No briefing generated yet.</p>
                      <Button onClick={generateInsight} disabled={insightLoading} size="sm" className="mt-3 bg-purple-600 hover:bg-purple-700">
                        {insightLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                        Generate now
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Hot accounts quick list */}
              {increasing.length > 0 && (
                <Card className="border-emerald-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-emerald-700"><Zap className="w-4 h-4" />Hot accounts — activity increasing</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {increasing.sort((a, b) => b.change_pct - a.change_pct).slice(0, 6).map(a => (
                        <div key={a.domain} className="p-3 bg-white rounded-lg border border-emerald-100 cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setActiveTab("accounts"); fetchAccountDetail(a.domain); }}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-sm text-slate-800">{a.company_name}</span>
                            <TrendBadge trend={a.trend} pct={a.change_pct} />
                          </div>
                          <RecommendationBadge rec={a.recommendation} />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* ACCOUNTS TAB */}
        <TabsContent value="accounts" className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search accounts..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="search-abm-input" />
              {searchQuery && <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="w-4 h-4 text-slate-400" /></button>}
            </div>
            <Button onClick={fetchData} size="sm" variant="outline"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
            <Button onClick={() => exportCSV("accounts")} size="sm" variant="outline"><Download className="w-4 h-4 mr-1" />CSV</Button>
          </div>

          <Card>
            <CardContent className="pt-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="abm-accounts-table">
                  <thead>
                    <tr className="border-b text-left text-xs text-slate-500 uppercase tracking-wider">
                      <th className="pb-3 pr-3">Account</th>
                      <th className="pb-3 pr-3 text-center">Trend</th>
                      <th className="pb-3 pr-3 text-center">Score</th>
                      <th className="pb-3 pr-3 text-center">Signals (30d)</th>
                      <th className="pb-3 pr-3 text-center">Prior (30d)</th>
                      <th className="pb-3 pr-3 text-center">AR impr.</th>
                      <th className="pb-3 pr-3 text-center">HS contacts</th>
                      <th className="pb-3 pr-3">Next best action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAccounts.map(a => (
                      <tr key={a.domain} className={`border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors ${selectedAccount === a.domain ? 'bg-blue-50' : ''}`} onClick={() => fetchAccountDetail(a.domain)} data-testid={`abm-row-${a.domain}`}>
                        <td className="py-2.5 pr-3">
                          <div className="font-medium text-slate-800">{a.company_name}</div>
                          <div className="text-[10px] text-slate-400">{a.domain}</div>
                        </td>
                        <td className="py-2.5 pr-3 text-center"><TrendBadge trend={a.trend} pct={a.change_pct} /></td>
                        <td className="py-2.5 pr-3 text-center"><Badge variant="secondary" className="font-mono text-xs">{Math.round(a.total_score)}</Badge></td>
                        <td className="py-2.5 pr-3 text-center font-medium">{a.recent_signals}</td>
                        <td className="py-2.5 pr-3 text-center text-slate-400">{a.prior_signals}</td>
                        <td className="py-2.5 pr-3 text-center">{a.ar_impressions ? a.ar_impressions.toLocaleString() : "—"}</td>
                        <td className="py-2.5 pr-3 text-center">{a.hs_contact_count || "—"}</td>
                        <td className="py-2.5 pr-3">
                          <div className={`text-xs font-medium px-2 py-1 rounded-full inline-flex items-center gap-1 ${a.recommendation?.badge === 'green' ? 'bg-emerald-50 text-emerald-700' : a.recommendation?.badge === 'red' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                            {a.recommendation?.badge === 'green' ? <Zap className="w-3 h-3" /> : a.recommendation?.badge === 'red' ? <TrendingDown className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                            {a.recommendation?.campaign_type}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Drill-down */}
          {selectedAccount && accountDetail && (
            <Card className="border-blue-200 bg-gradient-to-br from-blue-50/50 to-white" data-testid="abm-account-detail">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{accountDetail.company_name} <span className="text-sm text-slate-400 font-normal ml-2">{accountDetail.domain}</span></CardTitle>
                  <Button size="sm" variant="ghost" onClick={() => { setSelectedAccount(null); setAccountDetail(null); }}><X className="w-4 h-4" /></Button>
                </div>
                {accounts.find(a => a.domain === selectedAccount) && (
                  <div className="mt-2"><RecommendationBadge rec={accounts.find(a => a.domain === selectedAccount).recommendation} /></div>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-orange-600 uppercase tracking-wider flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-orange-400" />HubSpot</h4>
                    {accountDetail.hubspot?.contacts?.length > 0 ? accountDetail.hubspot.contacts.map((c, i) => (
                      <div key={i} className="text-xs bg-white rounded-lg p-3 border shadow-sm">
                        <div className="font-medium text-slate-800">{c.name || c.email}</div>
                        <div className="text-slate-500 mt-0.5">{c.title}</div>
                        <Badge variant="outline" className="mt-1 text-[10px]">{c.lifecycle}</Badge>
                      </div>
                    )) : <p className="text-xs text-slate-400">No contacts</p>}
                  </div>
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-blue-600 uppercase tracking-wider flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-400" />AdRoll</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {[{ l: "Impressions", v: (accountDetail.adroll?.total_impressions || 0).toLocaleString() }, { l: "Clicks", v: accountDetail.adroll?.total_clicks || 0 }, { l: "Spend", v: `$${(accountDetail.adroll?.total_cost || 0).toFixed(2)}` }, { l: "CTR", v: accountDetail.adroll?.total_impressions ? `${((accountDetail.adroll.total_clicks / accountDetail.adroll.total_impressions) * 100).toFixed(2)}%` : "—" }].map((m, i) => (
                        <div key={i} className="bg-white rounded-lg p-3 border shadow-sm text-center"><div className="text-lg font-bold text-slate-800">{m.v}</div><div className="text-[10px] text-slate-400 uppercase">{m.l}</div></div>
                      ))}
                    </div>
                    {accountDetail.adroll?.metrics?.length > 1 && (
                      <ResponsiveContainer width="100%" height={100}>
                        <AreaChart data={[...accountDetail.adroll.metrics].reverse()}><Area type="monotone" dataKey="impressions" stroke="#3B82F6" fill="#DBEAFE" strokeWidth={2} /><Tooltip formatter={v => [v.toLocaleString(), "Impressions"]} /></AreaChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-purple-600 uppercase tracking-wider flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-purple-400" />CommonRoom <Badge variant="secondary" className="text-[10px] ml-auto">{accountDetail.commonroom?.total_signals || 0}</Badge></h4>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                      {(accountDetail.commonroom?.signals || []).slice(0, 10).map((s, i) => (
                        <div key={i} className="text-xs bg-white rounded-lg p-2.5 border shadow-sm">
                          <div className="flex items-center justify-between"><Badge variant="outline" className="text-[10px]">{s.event_type}</Badge><span className="text-slate-300 text-[10px]">{new Date(s.received_at).toLocaleDateString()}</span></div>
                          {s.contact_name && <div className="text-slate-700 mt-1 font-medium">{s.contact_name}</div>}
                          {s.activity_title && <div className="text-slate-500 mt-0.5">{s.activity_title}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* SETTINGS TAB */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Settings className="w-4 h-4" />ABM thresholds</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-slate-500">These thresholds control when the agent recommends different campaign types. Adjust based on your account activity volume.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-700 mb-1 block">Medium activity threshold (signals/month)</label>
                  <p className="text-[10px] text-slate-400 mb-1">Accounts above this with increasing trend → Consideration ads</p>
                  <Input type="number" value={thresholds.medium_signals} onChange={e => setThresholds({ ...thresholds, medium_signals: parseInt(e.target.value) || 0 })} data-testid="threshold-medium" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700 mb-1 block">High activity threshold (signals/month)</label>
                  <p className="text-[10px] text-slate-400 mb-1">Accounts above this with fast growth → Lead Gen ads</p>
                  <Input type="number" value={thresholds.high_signals} onChange={e => setThresholds({ ...thresholds, high_signals: parseInt(e.target.value) || 0 })} data-testid="threshold-high" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700 mb-1 block">Growth rate trigger (%)</label>
                  <p className="text-[10px] text-slate-400 mb-1">MoM increase percentage to trigger Lead Gen recommendation</p>
                  <Input type="number" value={thresholds.growth_rate_trigger} onChange={e => setThresholds({ ...thresholds, growth_rate_trigger: parseInt(e.target.value) || 0 })} data-testid="threshold-growth" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700 mb-1 block">Thrashing threshold (x volatility)</label>
                  <p className="text-[10px] text-slate-400 mb-1">Swing multiplier that triggers a "caution" flag</p>
                  <Input type="number" value={thresholds.thrashing_threshold} onChange={e => setThresholds({ ...thresholds, thrashing_threshold: parseInt(e.target.value) || 0 })} data-testid="threshold-thrashing" />
                </div>
              </div>
              <Button onClick={saveThresholds} disabled={savingThresholds} size="sm" data-testid="save-thresholds-btn">
                {savingThresholds ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : null}Save thresholds
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
